import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import {arrayOf, func, node, shape, string} from 'prop-types';
import React, {useCallback, useMemo, useState} from 'react';

/**
 * Text-style split control: primary action uses `getHref(selected)`,
 * menu lists each format as a direct download link and updates the selection.
 */
function DownloadButton({
  getHref,
  formats,
  initialValue,
  tooltipTitle,
  icon,
  'aria-label': ariaLabel,
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [selected, setSelected] = useState(initialValue);
  const open = Boolean(anchorEl);

  const primaryHref = useMemo(() => getHref(selected), [getHref, selected]);

  const handleOpenMenu = useCallback(event => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleCloseMenu = useCallback(() => {
    setAnchorEl(null);
  }, []);

  return (
    <Box
      component="span"
      sx={{display: 'inline-flex', verticalAlign: 'middle', marginLeft: 1.5}}
    >
      <Tooltip title={tooltipTitle}>
        <ButtonGroup
          variant="text"
          size="small"
          sx={{
            '& .MuiButton-root': {
              minWidth: 0,
              px: 0.5,
              py: 0.25,
              color: 'primary.main',
            },
          }}
        >
          <Button
            component="a"
            href={primaryHref}
            download
            target="_blank"
            rel="noopener noreferrer"
            aria-label={ariaLabel}
          >
            {icon}
          </Button>
          <Button
            type="button"
            aria-label="Choose download format"
            aria-haspopup="true"
            aria-expanded={open ? 'true' : undefined}
            onClick={handleOpenMenu}
          >
            <ArrowDropDownIcon sx={{fontSize: 18}} />
          </Button>
        </ButtonGroup>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleCloseMenu}
        anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
        transformOrigin={{vertical: 'top', horizontal: 'right'}}
        slotProps={{list: {dense: true}}}
      >
        {formats.map(({value, label}) => (
          <MenuItem
            key={value}
            component="a"
            href={getHref(value)}
            download
            target="_blank"
            rel="noopener noreferrer"
            selected={value === selected}
            onClick={() => {
              setSelected(value);
              handleCloseMenu();
            }}
          >
            {label}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
}

DownloadButton.propTypes = {
  getHref: func.isRequired,
  formats: arrayOf(
    shape({
      value: string.isRequired,
      label: string.isRequired,
    }),
  ).isRequired,
  initialValue: string.isRequired,
  tooltipTitle: node,
  icon: node.isRequired,
  'aria-label': string,
};

export default DownloadButton;
